import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UtensilsCrossed, ArrowUpRight, Star } from 'lucide-react';
import { postsAPI } from '../api';

/**
 * Food & Drink Reviews — homepage strip.
 * Pulls the latest 5 posts in category `food-drink` and renders them as a
 * warm-toned "Latest reviews" section between NetworkBadge and Footer.
 */

const dateLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const FoodDrinkReviews = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    postsAPI.getAll('food-drink', 5, false)  // fetch latest 5 reviews (any image host)
      .then((r) => { if (alive) setPosts(r.data || []); })
      .catch(() => { if (alive) setPosts([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Hide the section entirely when there's nothing to show yet — avoids an
  // empty state on a fresh install.
  if (!loading && posts.length === 0) return null;

  const [feature, ...rest] = posts;

  return (
    <section
      className="relative py-16 md:py-20 bg-gradient-to-br from-amber-50 via-orange-50/50 to-rose-50/40"
      data-testid="home-food-drink-reviews"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <p className="text-xs font-semibold text-amber-700 uppercase tracking-[0.15em] mb-1 flex items-center gap-2">
              <UtensilsCrossed size={14} /> Local reviews
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" data-testid="home-food-drink-h2">
              Food & Drink in Paisley
            </h2>
            <p className="text-slate-600 mt-2 max-w-xl text-sm md:text-base">
              Honest, first-hand reviews of the town&apos;s cafes, restaurants, pubs and takeaways — from the paisley.org.uk team.
            </p>
          </div>
          <Link
            to="/food-drink"
            className="hidden md:inline-flex items-center gap-1.5 bg-white text-amber-700 border border-amber-200 hover:bg-amber-50 hover:border-amber-300 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition"
            data-testid="home-food-drink-cta"
          >
            All reviews <ArrowUpRight size={14} />
          </Link>
        </div>

        {loading ? (
          <div className="grid gap-6 md:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={`bg-white/70 rounded-2xl h-64 animate-pulse ${i === 0 ? 'md:col-span-3' : ''}`} />
            ))}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-5">
            {/* Feature */}
            {feature && (
              <Link
                to={`/news/${feature.slug}`}
                className="group md:col-span-3 relative rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-lg transition-shadow"
                data-testid={`food-drink-feature-${feature.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden bg-gradient-to-br from-amber-200 via-orange-200 to-rose-200">
                  {feature.featured_image && (
                    <img
                      src={feature.featured_image}
                      alt={feature.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="p-5 md:p-6">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-amber-700 font-semibold mb-2">
                    <Star size={12} className="fill-amber-500 stroke-amber-500" />
                    Latest review
                    <span className="text-slate-400 font-normal ml-auto">{dateLabel(feature.created_at)}</span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-slate-900 group-hover:text-amber-700 transition">
                    {feature.title}
                  </h3>
                  {feature.excerpt && (
                    <p className="text-slate-600 mt-2 line-clamp-2 text-sm md:text-base">{feature.excerpt}</p>
                  )}
                  <div className="mt-4 inline-flex items-center gap-1.5 text-amber-700 font-semibold text-sm">
                    Read review <ArrowUpRight size={14} />
                  </div>
                </div>
              </Link>
            )}

            {/* Rail */}
            {rest.length > 0 && (
              <div className="md:col-span-2 grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-1" data-testid="food-drink-rail">
                {rest.slice(0, 4).map((p) => (
                  <Link
                    key={p.id || p.slug}
                    to={`/news/${p.slug}`}
                    className="group flex gap-3 bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                    data-testid={`food-drink-item-${p.slug}`}
                  >
                    <div className="w-24 h-24 flex-shrink-0 bg-gradient-to-br from-amber-100 to-rose-100 overflow-hidden">
                      {p.featured_image && (
                        <img src={p.featured_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                      )}
                    </div>
                    <div className="flex-1 p-2.5 pr-3 min-w-0">
                      <div className="text-[10px] text-amber-700 font-semibold uppercase tracking-wider">{dateLabel(p.created_at)}</div>
                      <div className="font-semibold text-slate-900 leading-tight text-sm line-clamp-2 group-hover:text-amber-700 transition">{p.title}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        <Link
          to="/food-drink"
          className="md:hidden mt-6 inline-flex items-center gap-1.5 text-amber-700 font-semibold text-sm"
        >
          All reviews <ArrowUpRight size={14} />
        </Link>
      </div>
    </section>
  );
};

export default FoodDrinkReviews;
